import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import { Duration } from "luxon";
import { useEffect, useRef } from "react";

import { myPresencePing } from "../../services/api.service.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAmIInOffice } from "./use-am-i-in-office.js";

const PING_INTERVAL_MS = Duration.fromObject({ minutes: 5 }).toMillis();
const MAX_JITTER_MS = Duration.fromObject({ minutes: 1 }).toMillis();

interface UsePresencePingParams {
  availability: UserAvailability | null;
  msTeamId: string | null;
  enabled?: boolean;
}

/**
 * Hook to periodically ping the user's presence to the backend.
 * This helps track user activity and determine when to remove inactive users.
 */
export function usePresencePing({
  availability,
  msTeamId,
  enabled = true,
}: UsePresencePingParams) {
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setModalVariant } = useLayoutStore();

  // Once the user has been kicked for inactivity we must stop pinging. Otherwise
  // the next ping would re-upsert a fresh presence row (updatedAt = NOW()),
  // making the user look active in the DB again while Fluid still has them as
  // offline/kicked — a zombie state where they show offline to everyone, never
  // get re-kicked, and keep polling. The page reloads on rejoin anyway.
  //
  // We read this from the Fluid `isOnline` flag (the authoritative kick signal
  // set by the backend) rather than from whether the kick modal is open.
  const isInOffice = useAmIInOffice();

  useEffect(() => {
    // Only ping while the user is genuinely an active occupant of the office:
    // - `enabled`/`msTeamId`: we have a team context to ping for.
    // - `isInOffice`: not already kicked (avoids re-upserting a zombie session).
    if (!(enabled && msTeamId && isInOffice && availability)) {
      return;
    }

    let cancelled = false;
    const sendPing = async () => {
      try {
        const response = await myPresencePing({ availability, msTeamId });

        if (
          !cancelled &&
          response.notification &&
          response.notification.type === "inactivity-warning"
        ) {
          // Show inactivity warning modal
          setModalVariant("inactivity_warning", {
            remainingMinutes: response.notification.params.remainingMinutes,
          });
        }
        console.log("[usePresencePing] Ping sent successfully", {
          availability,
          msTeamId,
        });
      } catch (error) {
        recordErrorInSentry(error);
        console.warn("[usePresencePing] Failed to send ping:", error);
      }
    };

    // Send initial ping immediately so backend records current session quickly
    sendPing();

    // Set up interval for subsequent pings with random jitter per client
    const jitter = Math.floor(Math.random() * MAX_JITTER_MS);
    const intervalDuration = PING_INTERVAL_MS + jitter;
    intervalRef.current = setInterval(sendPing, intervalDuration);

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [availability, msTeamId, enabled, isInOffice, setModalVariant]);
}
